export interface CaseStudy {
  id: string;
  tag: string;
  tagColor: string;
  title: string;
  subtitle: string;
  industry: string;
  teamSize: string;
  duration: string;
  coverImage: string;
  client: {
    name: string;
    desc: string;
    scale: string;
    tech: string;
  };
  problem: {
    summary: string;
    details: string[];
  };
  whyHard: {
    summary: string;
    details: string[];
  };
  aiApproach: {
    summary: string;
    steps: { title: string; desc: string; icon: string }[];
  };
  execution: {
    phases: { phase: string; title: string; duration: string; actions: string[] }[];
  };
  result: {
    summary: string;
    highlights: { label: string; desc: string }[];
    quote: string;
    quoteAuthor: string;
  };
}

export const cases: CaseStudy[] = [
  {
    id: 'enterprise-saas-workshop',
    tag: '研发体系 AI 化',
    tagColor: '#2563EB',
    title: '某企业 SaaS 团队 研发体系 AI 化工作坊',
    subtitle: '20+ 人多角色集中参训，建立端到端 AI 研发工作流并完成首批资产沉淀',
    industry: '企业服务 SaaS',
    teamSize: '客户侧 20+ 人参训',
    duration: '10 个工作日集中工作坊 + 后续远程支持',
    coverImage: '/case-saas-office.jpg',
    client: {
      name: '某 B 端企业服务团队',
      desc: '面向企业客户的 SaaS 团队，业务系统持续迭代多年，希望把 AI 协作从个别人的尝试升级为团队级别的研发体系。',
      scale: '研发、测试、前端、后端、架构、管理多角色覆盖',
      tech: '多项目并行的成熟技术栈，含 Web、桌面端和测试自动化场景',
    },
    problem: {
      summary:
        '已经买了 AI 编码工具，但效果因人而异：写代码快了，但流程没变；个别工程师摸出了一些用法，但无法成为团队的稳定生产力。',
      details: [
        'AI 工具的使用停留在个人层面，团队整体生产力没有形成可观测的提升',
        '团队对"AI 应该在研发流程里扮演什么角色"没有共识，分工和职责不清',
        '测试环节在 AI 提效后明显承压：代码生成快了，传统 Code Review 跟不上',
        '希望尽快验证一套可复用的方法，但又担心脱离真实业务做演示项目没有意义',
      ],
    },
    whyHard: {
      summary:
        '把 AI 工具叠加到传统研发流程上，短期有效，中期一定遇到瓶颈——四个条件（认知 / 行动 / 检测 / 发布）现状只满足"行动"一项。',
      details: [
        '认知缺失：AI 拿不到 AI 可读的项目上下文，难以输出有业务策略含义的代码',
        '检测缺失：缺乏自动化验证闭环，AI 生成的产出无法被快速判定对错',
        '发布缺失：验证通过的产出还需要大量手工动作才能进入主干',
        '组织上的"职能墙"切断了 AI 能打通的端到端流程，全靠个人努力无法打破',
      ],
    },
    aiApproach: {
      summary:
        '以 Workshop 形式进入团队，三位一体推进：环境重构 → 实战带教 → 资产沉淀。坚持在客户真实的生产项目上手把手指导，不另起炉灶做演示项目。',
      steps: [
        {
          title: '建立 AI 可读的工程基线',
          desc: '在现有代码库上配置 CLAUDE.md、Skills、Rules、工程规范——一次性配置，跨项目复用，让 AI 真正理解项目上下文。',
          icon: 'ri-file-code-line',
        },
        {
          title: '端到端流水线跑通',
          desc: '把从需求拆解、文档生成、代码实现、编译、部署、调试、日志、提交，整条链路在真实项目上跑通一次，让团队亲眼看到"AI 落地的样子"。',
          icon: 'ri-flow-chart',
        },
        {
          title: '分层覆盖与角色分工',
          desc: '架构师批 / 工程师批 / 测试批分层推进。少数核心成员负责工具链沉淀，其他人按规范使用——这是规模化提效的现实路径。',
          icon: 'ri-team-line',
        },
        {
          title: '测试治理与质量门禁',
          desc: '"AI 生成文档，人工审核文档"作为 Code Review 的新工作方式，配合自动化测试和流水线门禁，避免"代码生成快但质量失控"。',
          icon: 'ri-shield-check-line',
        },
      ],
    },
    execution: {
      phases: [
        {
          phase: 'Phase 1',
          title: '项目熟悉与现状盘点',
          duration: 'Day 1–2',
          actions: [
            '梳理团队现有研发流程与工具栈，识别 AI 介入的优先入口',
            '统一"AI 时代研发效率"的判断框架（四个条件 / 三重锁死）',
            '盘点现有代码、文档、业务规则，确认本次要打透的真实项目目标',
          ],
        },
        {
          phase: 'Phase 2',
          title: '环境重构',
          duration: 'Day 3–5',
          actions: [
            '在示范项目中落地 CLAUDE.md、Skills、Rules、流水线规范',
            '建立 AI 可读的文档体系（业务规则、接口契约、模块边界）',
            '搭建自动化检测闭环（编译、单元测试、流水线钩子）',
          ],
        },
        {
          phase: 'Phase 3',
          title: '实战带教',
          duration: 'Day 6–9',
          actions: [
            '在真实项目上推进端到端的 AI 协作流水线',
            '架构师 / 工程师 / 测试多角色一起跑通',
            '现场处理真实出现的边界、冲突和误判',
            '逐步把主导权从我们交还团队，直到能独立运转',
          ],
        },
        {
          phase: 'Phase 4',
          title: '收尾验收 + 远程支持',
          duration: 'Day 10 + 16h 远程',
          actions: [
            '资产清点：Skills / Rules / Agents / 工作流脚本完整交付',
            '关键节点演示与可复现性验证',
            '后续 16 小时远程支持：解答、小修正、迁移咨询',
            '团队自评与下一步路线建议',
          ],
        },
      ],
    },
    result: {
      summary:
        '团队认知从"个人用 AI 提效"转向"组织建设 AI 研发体系"。环境配置、技能模板和流水线规范可以直接迁移到自有项目；测试组已具备在内部推广 AI 回归测试的条件。',
      highlights: [
        { label: '流程跑通', desc: '从需求到提交真实跑通，不是 PPT 演示' },
        { label: '资产可带走', desc: 'CLAUDE.md、Skills、Rules、流水线规范跨项目复用' },
        { label: '门槛降低', desc: '不要求每位工程师都深入研究 AI 工具底层即可落地' },
        { label: '角色覆盖', desc: '架构、研发、测试、前端、后端、管理全角色受益' },
      ],
      quote:
        '培训的重点不在具体技术细节，而是在统一一个方向：AI 时代研发效率的核心是端到端 AI 工具链，而不是个人使用 AI 的能力。这一点对团队分工是一个比较大的认知转变。',
      quoteAuthor: '— 研发总监（团队约 50 人）',
    },
  },
  {
    id: 'security-onsite-pilot',
    tag: '5 天驻场快速诊断',
    tagColor: '#F97316',
    title: '某网络安全软件团队 5 天驻场快速诊断',
    subtitle: '在 5 个工作日内，跑通自动化测试、IT 自动化运维与团队工具链的初始版本',
    industry: '网络安全软件',
    teamSize: '客户侧多角色配合（研发 + QA + IT）',
    duration: '5 个工作日驻场',
    coverImage: '/case-fintech-tech.jpg',
    client: {
      name: '某安全产品研发团队',
      desc: '面向企业市场的安全软件团队，长期面临测试覆盖不足与 IT 部署运维消耗大量人力的现实压力。',
      scale: '研发、QA、IT 多职能配合',
      tech: '已有完整产品线与既有测试项目，IT 运维自动化基础薄弱',
    },
    problem: {
      summary:
        '希望快速判断 AI 是否能在自动化测试与 IT 运维两条线上形成可见的提效抓手，而不是签一个长周期项目再慢慢看效果。',
      details: [
        'QA 团队希望在人工引导下利用 AI 自动生成测试管理，提升测试覆盖度',
        'IT 团队希望通过 AI 赋能，节省部署与维护中的大量人力消耗',
        '研发团队希望有一份"VSCode + Claude Code"的统一插件与配置参考',
        '团队成员的开发机型号和系统不一，需要兼容性较强的方案',
      ],
    },
    whyHard: {
      summary: '短周期、多目标、多角色的并行推进，对培训方的方法论密度与现场调度能力要求较高。',
      details: [
        '5 天内同时覆盖测试自动化、IT 自动化、工具链统一三条主线，时间紧',
        '已有自动化测试项目需要先盘点再决定保留 / 重构方式',
        '远程开发模式（服务器 + 终端复用）对部分成员是新概念',
        '团队对 AI 工具的差异和适配性缺乏统一判断口径',
      ],
    },
    aiApproach: {
      summary: '采用"先评估、再起底、留可演进物"的现场推进方式，把 5 天压在三条主线上的可见抓手上。',
      steps: [
        {
          title: '存量测试资产评估',
          desc: '完成已有自动化测试项目的解析与评估，识别可保留、可重构、可替换的部分，给后续工作划清边界。',
          icon: 'ri-search-line',
        },
        {
          title: '新自动化测试样板',
          desc: '新建自动化测试项目，跑通登录等基础页面的加载测试，并支持按指令截图与截取视频，自动生成测试报告。',
          icon: 'ri-camera-3-line',
        },
        {
          title: '自动化 IT 运维原型',
          desc: '完成自动化远程部署项目的初始化，跑通基本流程；为 IT 团队提供后续可持续演进的工作基线。',
          icon: 'ri-server-line',
        },
        {
          title: '团队工具链统一',
          desc: '产出一份团队通用的 VSCode、Claude Code 插件列表与配置文档，降低跨成员的环境差异和上手成本。',
          icon: 'ri-tools-line',
        },
      ],
    },
    execution: {
      phases: [
        {
          phase: 'Day 1',
          title: '现场盘点与目标对齐',
          duration: '1 天',
          actions: [
            '梳理 QA / IT / 研发各侧的痛点与可投入资源',
            '盘点已有自动化测试项目和部署脚本现状',
            '锁定 5 天主线：自动化测试 + 自动化运维 + 工具链统一',
          ],
        },
        {
          phase: 'Day 2-3',
          title: '自动化测试落地',
          duration: '2 天',
          actions: [
            '新建自动化测试项目，跑通基础页面加载测试',
            '配置截图、截取视频与测试报告自动生成',
            '约定后续 QA 团队的可演进路径',
          ],
        },
        {
          phase: 'Day 4',
          title: 'IT 自动化运维',
          duration: '1 天',
          actions: [
            '初始化自动化远程部署项目',
            '跑通基础部署与运维流程',
            '与现场 IT 同事完成关键操作的现场演示',
          ],
        },
        {
          phase: 'Day 5',
          title: '工具链统一与收尾',
          duration: '1 天',
          actions: [
            '产出团队通用 VSCode、Claude Code 插件清单与配置说明',
            '把 5 天产出整理为可由团队继续推进的基线文档',
            '答疑、交接与后续路径建议',
          ],
        },
      ],
    },
    result: {
      summary:
        '在 5 个工作日内同时把"自动化测试样板 / 自动化部署原型 / 团队统一工具链"三件事的初始版本立了起来，给团队后续在 AI 测试与 IT 运维两个方向继续投入提供了可演进的起点。',
      highlights: [
        { label: '测试样板', desc: '基础页面加载测试与报告自动生成已可演示' },
        { label: '部署原型', desc: '远程自动化部署的基本流程跑通，可由 IT 接续' },
        { label: '工具链文档', desc: '团队级 VSCode / Claude Code 插件与配置统一' },
        { label: '可演进', desc: '产出物以"基线 + 后续路径"形态交付，便于团队自主推进' },
      ],
      quote:
        '5 天里我们没有追求一次性把所有事情做完，而是把每一条主线都跑通到团队后续可以自己接着走的程度。这是我们对短周期驻场最务实的定义。',
      quoteAuthor: '— 顾问侧总结',
    },
  },
];
